import React, { useEffect, useMemo, useState } from 'react';
import { Save, Plus, X, Trash2, Upload, Image as ImageIcon, Loader as Loader2, Download } from 'lucide-react';
import { KioskService } from '../../services/KioskService';
import { supabase } from '../../lib/supabase';

export const inputCls = 'w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white';

export const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-300 mb-1">{label}</label>
    {children}
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

export const ColorInput: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
  <div className="flex items-center gap-2">
    <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-12 h-10 bg-slate-900 border border-slate-700 rounded cursor-pointer" />
    <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} font-mono`} />
  </div>
);

export const TabBtn: React.FC<{ active: boolean; onClick: () => void; icon: React.ReactNode; label: string }> = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition ${
      active ? 'border-sky-400 text-white' : 'border-transparent text-slate-400 hover:text-white'
    }`}
  >
    {icon} {label}
  </button>
);

export const ImageUpload: React.FC<{
  leagueId: string;
  kind: string;
  value: string;
  onChange: (url: string) => void;
  previewHeight?: string;
}> = ({ leagueId, kind, value, onChange, previewHeight = 'h-20' }) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setErr(null);
    setUploading(true);
    try {
      const url = await KioskService.adminUploadAsset(leagueId, file, kind);
      onChange(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div className={`${previewHeight} w-32 bg-slate-900/60 border border-slate-700 rounded-lg flex items-center justify-center overflow-hidden`}>
        {value ? (
          <img src={value} alt="Preview" className="max-h-full max-w-full object-contain" />
        ) : (
          <ImageIcon className="w-6 h-6 text-slate-600" />
        )}
      </div>
      <div className="flex-1">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
            if (inputRef.current) inputRef.current.value = '';
          }}
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 px-3 py-2 bg-sky-500/20 hover:bg-sky-500/30 text-sky-200 rounded-lg text-sm disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Uploading...' : value ? 'Replace' : 'Upload'}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="text-xs text-slate-400 hover:text-red-300"
            >
              Remove
            </button>
          )}
        </div>
        {err && <div className="text-xs text-red-400 mt-1">{err}</div>}
      </div>
    </div>
  );
};

export const SponsorUploader: React.FC<{ onUpload: (file: File) => Promise<void> }> = ({ onUpload }) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          setErr(null);
          setUploading(true);
          try {
            await onUpload(f);
          } catch (e) {
            setErr(e instanceof Error ? e.message : 'Upload failed');
          } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = '';
          }
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="inline-flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm disabled:opacity-50"
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        Add sponsor logo
      </button>
      {err && <div className="text-xs text-red-400 mt-1">{err}</div>}
    </div>
  );
};

export const BrandingTab: React.FC<{ league: Record<string, any>; onSave: (patch: Record<string, unknown>) => void; saving: boolean }> = ({ league, onSave, saving }) => {
  const leagueId = league.id as string;
  const [form, setForm] = useState({
    primary_color: league.primary_color || '#0ea5e9',
    secondary_color: league.secondary_color || '#0284c7',
    accent_color: league.accent_color || '#f59e0b',
    logo_url: league.logo_url || '',
    hero_image_url: league.hero_image_url || '',
    background_image_url: league.background_image_url || '',
    sponsor_logos: (league.sponsor_logos as string[]) || [],
    sponsor_links: (league.sponsor_links as string[]) || [],
    signup_headline: league.signup_headline || '',
    signup_subtext: league.signup_subtext || '',
    terms_text: league.terms_text || '',
  });

  const addSponsorLogo = async (file: File) => {
    const url = await KioskService.adminUploadAsset(leagueId, file, 'sponsor');
    setForm((f) => ({
      ...f,
      sponsor_logos: [...f.sponsor_logos, url],
      sponsor_links: [...f.sponsor_links, ''],
    }));
  };

  const removeSponsor = (index: number) => {
    setForm((f) => ({
      ...f,
      sponsor_logos: f.sponsor_logos.filter((_, i) => i !== index),
      sponsor_links: f.sponsor_links.filter((_, i) => i !== index),
    }));
  };

  const setSponsorLink = (index: number, value: string) => {
    setForm((f) => ({
      ...f,
      sponsor_links: f.sponsor_links.map((l, i) => (i === index ? value : l)),
    }));
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-5 bg-slate-800/60 border border-slate-700 rounded-xl p-5 max-w-3xl">
      <div className="grid grid-cols-3 gap-4">
        <Field label="Primary Color">
          <ColorInput value={form.primary_color} onChange={(v) => setForm({ ...form, primary_color: v })} />
        </Field>
        <Field label="Secondary Color">
          <ColorInput value={form.secondary_color} onChange={(v) => setForm({ ...form, secondary_color: v })} />
        </Field>
        <Field label="Accent Color">
          <ColorInput value={form.accent_color} onChange={(v) => setForm({ ...form, accent_color: v })} />
        </Field>
      </div>

      <Field label="Logo" hint="PNG or SVG with transparent background works best.">
        <ImageUpload
          leagueId={leagueId}
          kind="logo"
          value={form.logo_url}
          onChange={(url) => setForm({ ...form, logo_url: url })}
          previewHeight="h-14"
        />
      </Field>

      <Field label="Hero Image" hint="Shown at the top of the signup page.">
        <ImageUpload
          leagueId={leagueId}
          kind="hero"
          value={form.hero_image_url}
          onChange={(url) => setForm({ ...form, hero_image_url: url })}
          previewHeight="h-28"
        />
      </Field>

      <Field label="Background Image" hint="Full-screen background behind the signup card.">
        <ImageUpload
          leagueId={leagueId}
          kind="background"
          value={form.background_image_url}
          onChange={(url) => setForm({ ...form, background_image_url: url })}
          previewHeight="h-28"
        />
      </Field>

      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">Sponsor Logos</label>
        <div className="space-y-2 mb-3">
          {form.sponsor_logos.length === 0 && (
            <p className="text-xs text-slate-500">No sponsors yet. Upload a logo below.</p>
          )}
          {form.sponsor_logos.map((logo, i) => (
            <div key={i} className="flex items-center gap-3 bg-slate-900/50 border border-slate-700 rounded-lg p-2">
              <img src={logo} alt="Sponsor" className="h-10 object-contain bg-white/5 rounded px-2" />
              <input
                type="url"
                value={form.sponsor_links[i] || ''}
                onChange={(e) => setSponsorLink(i, e.target.value)}
                placeholder="https://sponsor.example (optional)"
                className={`${inputCls} flex-1`}
              />
              <button
                type="button"
                onClick={() => removeSponsor(i)}
                className="p-2 text-red-400 hover:text-red-300"
                title="Remove sponsor"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <SponsorUploader onUpload={addSponsorLogo} />
      </div>

      <Field label="Signup Headline">
        <input type="text" value={form.signup_headline} onChange={(e) => setForm({ ...form, signup_headline: e.target.value })} className={inputCls} placeholder="Join the Summer Sing-Off!" />
      </Field>
      <Field label="Signup Subtext">
        <textarea value={form.signup_subtext} onChange={(e) => setForm({ ...form, signup_subtext: e.target.value })} className={`${inputCls} h-20`} />
      </Field>
      <Field label="Terms & Privacy Text">
        <textarea value={form.terms_text} onChange={(e) => setForm({ ...form, terms_text: e.target.value })} className={`${inputCls} h-24`} placeholder="By joining, you agree to..." />
      </Field>
      <button disabled={saving} type="submit" className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg disabled:opacity-50">
        <Save className="w-4 h-4" /> Save
      </button>
    </form>
  );
};

export const RulesTab: React.FC<{ league: Record<string, any>; onSave: (patch: Record<string, unknown>) => void; saving: boolean }> = ({ league, onSave, saving }) => {
  const [markdown, setMarkdown] = useState<string>(league.rules_markdown || '');

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-1">Contest Rules</h3>
        <p className="text-sm text-slate-400 mb-4">
          Write your contest rules using basic Markdown formatting. Supports headings (#), bold (**text**), lists (- item or 1. item), and links.
        </p>
        <textarea
          value={markdown}
          onChange={(e) => setMarkdown(e.target.value)}
          rows={18}
          className="w-full px-4 py-3 bg-slate-900 border border-slate-700 text-white rounded-lg text-sm font-mono leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-sky-500/50 focus:border-sky-500 placeholder-slate-500"
          placeholder={"# Contest Rules\n\n1. Each participant may submit one recording.\n2. Scores are based on pitch accuracy and timing.\n3. The highest score wins!\n\n**Good luck!**"}
        />
      </div>
      <button
        onClick={() => onSave({ rules_markdown: markdown })}
        disabled={saving}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-sky-500 hover:bg-sky-400 text-white transition disabled:opacity-50"
      >
        <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Rules'}
      </button>
    </div>
  );
};

export const SongsTab: React.FC<{ leagueId: string }> = ({ leagueId }) => {
  const [leagueSongs, setLeagueSongs] = useState<Array<Record<string, any>>>([]);
  const [allSongs, setAllSongs] = useState<Array<{ id: string; title: string; artist: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { load(); }, [leagueId]);

  const load = async () => {
    try {
      setLoading(true);
      const [ls, songsRes] = await Promise.all([
        KioskService.adminListLeagueSongs(leagueId),
        supabase.from('songs').select('id, title, artist').eq('is_deleted', false).eq('hide_from_kiosk', false).order('title').limit(5000),
      ]);
      setLeagueSongs(ls);
      setAllSongs((songsRes.data || []) as Array<{ id: string; title: string; artist: string }>);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const addedIds = useMemo(() => new Set(leagueSongs.map((s) => s.song_id)), [leagueSongs]);
  const filtered = allSongs
    .filter((s) => !addedIds.has(s.id))
    .filter((s) => !search || s.title.toLowerCase().includes(search.toLowerCase()) || s.artist?.toLowerCase().includes(search.toLowerCase()));

  const add = async (songId: string) => {
    try {
      await KioskService.adminAddLeagueSong(leagueId, songId, leagueSongs.length);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    }
  };

  const remove = async (id: string) => {
    try {
      await KioskService.adminRemoveLeagueSong(id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    }
  };

  if (loading) return <div className="text-slate-400">Loading...</div>;

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-3">Songs in League ({leagueSongs.length})</h3>
        {error && <div className="mb-2 p-2 bg-red-500/10 text-red-300 text-sm rounded">{error}</div>}
        {leagueSongs.length === 0 ? (
          <p className="text-slate-400 text-sm">No songs yet. Add from the catalog on the right.</p>
        ) : (
          <ul className="space-y-2 max-h-[500px] overflow-y-auto">
            {leagueSongs.map((ls) => (
              <li key={ls.id} className="flex items-center justify-between bg-slate-900/50 rounded-lg px-3 py-2">
                <div className="text-sm">
                  <div className="text-white">{ls.songs?.title}</div>
                  <div className="text-slate-400 text-xs">{ls.songs?.artist}</div>
                </div>
                <button onClick={() => remove(ls.id)} className="text-red-400 hover:text-red-300 p-1">
                  <X className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-3">Add from Catalog</h3>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search songs..."
          className={`${inputCls} mb-3`}
        />
        <ul className="space-y-1 max-h-[70vh] overflow-y-auto">
          {filtered.map((s) => (
            <li key={s.id} className="flex items-center justify-between bg-slate-900/50 rounded-lg px-3 py-2">
              <div className="text-sm">
                <div className="text-white">{s.title}</div>
                <div className="text-slate-400 text-xs">{s.artist}</div>
              </div>
              <button onClick={() => add(s.id)} className="text-sky-400 hover:text-sky-300 p-1">
                <Plus className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export const ParticipantsTab: React.FC<{ leagueId: string }> = ({ leagueId }) => {
  const [participants, setParticipants] = useState<Array<Record<string, any>>>([]);
  const [loading, setLoading] = useState(true);
  const [toReset, setToReset] = useState<Record<string, any> | null>(null);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<Record<string, any> | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => { load(); }, [leagueId]);
  const load = async () => {
    setLoading(true);
    const data = await KioskService.adminListParticipants(leagueId);
    setParticipants(data as Array<Record<string, any>>);
    setLoading(false);
  };

  const confirmReset = async () => {
    if (!toReset) return;
    setResetError(null);
    setResetting(true);
    try {
      await KioskService.adminDeleteParticipantSubmissions(toReset.id);
      setToReset(null);
      await load();
    } catch (e) {
      setResetError(e instanceof Error ? e.message : 'Failed to clear submissions');
    } finally {
      setResetting(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      await KioskService.adminDeleteParticipant(toDelete.id);
      setToDelete(null);
      await load();
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete participant');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="text-slate-400">Loading...</div>;

  const exportableParticipants = participants.filter(
    (p) => p.kiosk_user?.email && !p.kiosk_user.email.endsWith('.local')
  );

  const handleExportEmails = () => {
    const rows = exportableParticipants.map((p) => `${p.display_name},${p.kiosk_user.email},${p.kiosk_user?.marketing_opt_in ? 'Yes' : 'No'}`);
    const csv = ['Name,Email,Marketing Opted In', ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'participants-export.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {exportableParticipants.length > 0 && (
        <div className="flex justify-end">
          <button
            onClick={handleExportEmails}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 rounded-xl text-sm font-semibold transition"
          >
            <Download size={16} />
            Export Participants ({exportableParticipants.length})
          </button>
        </div>
      )}

    <div className="bg-slate-800/60 border border-slate-700 rounded-xl overflow-hidden">
      {participants.length === 0 ? (
        <div className="p-6 text-slate-400 text-center">No participants yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-900/50 text-slate-400">
            <tr>
              <th className="text-left p-3">Display Name</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Playback</th>
              <th className="text-left p-3">Joined</th>
              <th className="text-left p-3">Marketing</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {participants.map((p) => (
              <tr key={p.id} className="border-t border-slate-700">
                <td className="p-3 text-white">{p.display_name}</td>
                <td className="p-3 text-slate-300">{p.kiosk_user?.email}</td>
                <td className="p-3">
                  <select
                    value={p.status}
                    onChange={async (e) => {
                      await KioskService.adminUpdateParticipantStatus(p.id, e.target.value);
                      load();
                    }}
                    className="bg-slate-900 border border-slate-700 text-white rounded px-2 py-1 text-xs"
                  >
                    <option value="registered">Registered</option>
                    <option value="queued">Queued</option>
                    <option value="scored">Scored</option>
                    <option value="withdrawn">Withdrawn</option>
                    <option value="disqualified">Disqualified</option>
                  </select>
                </td>
                <td className="p-3 text-slate-300">{p.allow_playback ? 'Yes' : 'No'}</td>
                <td className="p-3 text-slate-400 text-xs">{new Date(p.joined_at).toLocaleDateString()}</td>
                <td className="p-3 text-slate-300">{p.kiosk_user?.marketing_opt_in ? 'Opted in' : '-'}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-1.5 justify-end">
                    {p.status === 'scored' && (
                      <button
                        onClick={() => { setResetError(null); setToReset(p); }}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 text-xs font-semibold transition"
                        title="Delete this participant's submission so they can re-record"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Reset
                      </button>
                    )}
                    <button
                      onClick={() => { setDeleteError(null); setToDelete(p); }}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 text-xs font-semibold transition"
                      title="Remove this participant from the league"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {toReset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-300" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Reset participant?</h3>
                <p className="text-slate-400 text-sm mt-1">
                  This deletes <span className="text-white font-semibold">{toReset.display_name}</span>'s submission(s) so they can record again. This cannot be undone.
                </p>
              </div>
            </div>

            {resetError && (
              <div className="mb-3 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2.5">
                {resetError}
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setToReset(null)}
                disabled={resetting}
                className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-200 border border-white/10 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmReset}
                disabled={resetting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white font-semibold disabled:opacity-60"
              >
                {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {resetting ? 'Clearing...' : 'Delete submission'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-300" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Remove participant?</h3>
                <p className="text-slate-400 text-sm mt-1">
                  This removes <span className="text-white font-semibold">{toDelete.display_name}</span> from the league along with any of their submissions. This cannot be undone.
                </p>
              </div>
            </div>

            {deleteError && (
              <div className="mb-3 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2.5">
                {deleteError}
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setToDelete(null)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-200 border border-white/10 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white font-semibold disabled:opacity-60"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {deleting ? 'Removing...' : 'Remove participant'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};
